import React, { useEffect, useState } from 'react';
import { View, FlatList, Text, StyleSheet, TouchableOpacity, Image, Dimensions, Modal } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getPhotosData } from '../../../api/Reservation';

interface Props {
  reservationInfo: any;
}

const PhotosGrid = ({reservationInfo}: Props) => {
  const [photosData, setPhotosData] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const screenWidth = Dimensions.get('window').width;
  const numColumns = 3;
  const horizontalPadding = 60; // Account for parent padding (30 * 2)
  const imageSpacing = 12;
  const imageSize = (screenWidth - horizontalPadding - (imageSpacing * (numColumns - 1))) / numColumns;
  const maxImageSize = 220;
  const finalImageSize = Math.min(imageSize, maxImageSize);

  useEffect(() => {
    getPhotosData({ order_number: reservationInfo.order_number }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setPhotosData(jsonRes);
          break;
        default:
          setPhotosData([]);
          break;
      }
    });
  }, [reservationInfo]);

  const openPhotoModal = (photo: string) => {
    setSelectedPhoto(photo);
    setModalVisible(true);
  };

  const closePhotoModal = () => {
    setModalVisible(false);
    setSelectedPhoto(null);
  };

  const renderPhotoItem = ({ item: photo, index }) => (
    <TouchableOpacity
      style={[
        styles.photoContainer, 
        { 
          width: finalImageSize, 
          height: finalImageSize,
          marginRight: (index + 1) % numColumns === 0 ? 0 : imageSpacing,
          marginBottom: imageSpacing,
        }
      ]}
      onPress={() => openPhotoModal(photo)}
    >
      <Image
        source={{ uri: photo }}
        style={styles.photo}
        resizeMode="cover"
      />
      <View style={styles.photoOverlay}>
        <FontAwesome5 name="expand" size={12} color="white" />
      </View>
    </TouchableOpacity>
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.photosHeader}>
        <FontAwesome5 name="images" size={18} color="#333" />
        <Text style={styles.photosCount}>
          {photosData.length} photo{photosData.length !== 1 ? 's' : ''}
        </Text>
      </View>
    </View>
  );

  const renderEmpty = () => (
    <View style={styles.emptyState}>
      <FontAwesome5 name="camera" size={48} color="#ccc" />
      <Text style={styles.emptyText}>No photos available</Text>
    </View>
  );

  return (
    <View style={styles.container}>
      <FlatList
        data={photosData}
        renderItem={renderPhotoItem}
        keyExtractor={(item, index) => index.toString()}
        numColumns={numColumns}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={styles.flatListContent}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
        style={styles.flatList}
      />

      {/* Full Screen Photo Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={closePhotoModal}
      >
        <View style={styles.modalContainer}>
          <TouchableOpacity 
            style={styles.modalBackground}
            onPress={closePhotoModal}
          >
            <View style={styles.modalContent}>
              {selectedPhoto && (
                <>
                  <Image
                    source={{ uri: selectedPhoto }}
                    style={styles.fullScreenPhoto}
                    resizeMode="contain"
                  />
                </>
              )}
              <TouchableOpacity 
                style={styles.closeButton}
                onPress={closePhotoModal}
              >
                <FontAwesome5 name="times" size={24} color="white" />
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'white',
  },
  flatList: {
    flex: 1,
    width: '100%',
  },
  flatListContent: {
    padding: 16,
    paddingBottom: 40,
  },
  header: {
    marginBottom: 16,
  },
  photosHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  photosCount: {
    fontSize: 16,
    marginLeft: 8,
    color: '#333',
    fontWeight: '500',
  },
  photoContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  photo: {
    width: '100%',
    height: '100%',
    backgroundColor: '#f0f0f0',
  },
  photoOverlay: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    minHeight: 200,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#999',
  },
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
  },
  modalBackground: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  fullScreenPhoto: {
    width: '90%',
    height: '70%',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default PhotosGrid;